import { supabase } from "@/integrations/supabase/client";

/**
 * Sends notifications to:
 * - All non-student users
 * - Students in the specified target classes (matched via registrations → courses.class)
 * Uses the secure send_bulk_notifications RPC to bypass RLS safely.
 */
export async function sendAcademicNotifications({
  title,
  message,
  link,
  targetClasses,
}: {
  title: string;
  message?: string;
  link?: string;
  targetClasses: string[];
}) {
  // 1. Get all non-student user IDs
  const { data: allRoles } = await supabase
    .from("user_roles")
    .select("user_id, role");

  const nonStudentUserIds = new Set<string>();
  const studentUserIds = new Set<string>();

  if (allRoles) {
    const userRolesMap = new Map<string, string[]>();
    for (const r of allRoles) {
      const existing = userRolesMap.get(r.user_id) || [];
      existing.push(r.role);
      userRolesMap.set(r.user_id, existing);
    }
    for (const [userId, roles] of userRolesMap) {
      if (roles.some((r) => r !== "student")) {
        nonStudentUserIds.add(userId);
      }
    }
  }

  // 2. Get student user IDs by class (via registrations → courses)
  if (targetClasses.length > 0) {
    const { data: courses } = await supabase
      .from("courses")
      .select("id, class")
      .in("class", targetClasses);

    if (courses && courses.length > 0) {
      const courseIds = courses.map((c) => c.id);
      const { data: registrations } = await supabase
        .from("registrations")
        .select("student_id")
        .in("course_id", courseIds);

      if (registrations && registrations.length > 0) {
        const studentIds = [...new Set(registrations.map((r) => r.student_id))];
        const { data: students } = await supabase
          .from("students")
          .select("user_id")
          .in("id", studentIds)
          .not("user_id", "is", null);

        if (students) {
          for (const s of students) {
            if (s.user_id) studentUserIds.add(s.user_id);
          }
        }
      }
    }
  }

  // 3. Combine and deduplicate
  const allUserIds = Array.from(new Set([...nonStudentUserIds, ...studentUserIds]));
  if (allUserIds.length === 0) return;

  // 4. Send via secure RPC in batches of 500
  for (let i = 0; i < allUserIds.length; i += 500) {
    const batch = allUserIds.slice(i, i + 500);
    await supabase.rpc("send_bulk_notifications", {
      _user_ids: batch,
      _title: title,
      _message: message || null,
      _type: "academic",
      _link: link || null,
    });
  }
}
